/**
 * Developers Page
 *
 * Enterprise features:
 * - API key management with create/revoke
 * - Webhook configuration with test functionality
 * - Real-time API logs with polling
 * - Secure key display with copy-to-clipboard
 * - Optimistic updates for mutations
 */

import { useState, useMemo, useCallback } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { apiKeysApi, webhooksApi, activityApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation, useInfiniteQuery, useIntersectionObserver } from '../../hooks/useDataFetch';
import './Developers.css';

// Constants
const LOGS_REFRESH_INTERVAL = 10000; // 10 seconds
const LOGS_PAGE_SIZE = 50;

// Webhook event types
const WEBHOOK_EVENTS = [
  { value: 'user.created', label: 'User Created' },
  { value: 'user.login', label: 'User Login' },
  { value: 'user.logout', label: 'User Logout' },
  { value: 'user.deleted', label: 'User Deleted' },
  { value: 'fraud.detected', label: 'Fraud Detected' },
  { value: 'fraud.blocked', label: 'Fraud Blocked' },
  { value: 'tenant.created', label: 'Tenant Created' },
  { value: 'tenant.suspended', label: 'Tenant Suspended' },
  { value: 'subscription.created', label: 'Subscription Created' },
  { value: 'subscription.cancelled', label: 'Subscription Cancelled' },
];

const Developers = () => {
  const [activeTab, setActiveTab] = useState('api-keys');
  const { showToast } = useToast();

  // Modal states
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [showCreateWebhookModal, setShowCreateWebhookModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newWebhook, setNewWebhook] = useState({ url: '', events: [] });
  const [createdKey, setCreatedKey] = useState(null);

  // Fetch API keys
  const {
    data: apiKeysData,
    isLoading: loadingKeys,
    refetch: refetchKeys,
  } = useQuery(
    () => apiKeysApi.list(),
    {
      key: 'developers:apiKeys',
      cacheTTL: 30000,
    }
  );

  // Fetch webhooks
  const {
    data: webhooksData,
    isLoading: loadingWebhooks,
    refetch: refetchWebhooks,
  } = useQuery(
    () => webhooksApi.list(),
    {
      key: 'developers:webhooks',
      cacheTTL: 30000,
    }
  );

  // Fetch API logs with cursor pagination and polling
  const {
    data: apiLogs,
    isLoading: loadingLogs,
    isFetchingMore,
    hasMore,
    fetchMore,
    refetch: refetchLogs,
  } = useInfiniteQuery(
    (cursor) => activityApi.list({
      cursor,
      limit: LOGS_PAGE_SIZE,
      type: 'api',
      sort: 'createdAt:desc',
    }),
    {
      key: 'developers:logs',
      getNextCursor: (page) => page?.nextCursor || page?.meta?.nextCursor,
      cacheTTL: 5000,
      refetchInterval: activeTab === 'logs' ? LOGS_REFRESH_INTERVAL : undefined,
    }
  );

  // Create API key mutation
  const createKeyMutation = useMutation(
    (name) => apiKeysApi.create({ name }),
    {
      onSuccess: (data) => {
        setCreatedKey(data.key || data.apiKey || data);
        setNewKeyName('');
        refetchKeys();
        showToast('API key created successfully', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to create API key', 'error');
      },
    }
  );

  // Revoke API key mutation
  const revokeKeyMutation = useMutation(
    (keyId) => apiKeysApi.revoke(keyId),
    {
      onSuccess: () => {
        refetchKeys();
        showToast('API key revoked', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to revoke API key', 'error');
      },
    }
  );

  // Create webhook mutation
  const createWebhookMutation = useMutation(
    (data) => webhooksApi.create(data),
    {
      onSuccess: () => {
        setShowCreateWebhookModal(false);
        setNewWebhook({ url: '', events: [] });
        refetchWebhooks();
        showToast('Webhook created successfully', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to create webhook', 'error');
      },
    }
  );

  // Delete webhook mutation
  const deleteWebhookMutation = useMutation(
    (webhookId) => webhooksApi.delete(webhookId),
    {
      onSuccess: () => {
        refetchWebhooks();
        showToast('Webhook deleted', 'success');
      },
      onError: (error) => {
        showToast(error.message || 'Failed to delete webhook', 'error');
      },
    }
  );

  // Test webhook mutation
  const testWebhookMutation = useMutation(
    (webhookId) => webhooksApi.test(webhookId),
    {
      onSuccess: (data) => {
        const status = data?.status || data?.statusCode || 200;
        if (status >= 200 && status < 300) {
          showToast(`Webhook test successful (${status})`, 'success');
        } else {
          showToast(`Webhook responded with ${status}`, 'warning');
        }
      },
      onError: (error) => {
        showToast(error.message || 'Webhook test failed', 'error');
      },
    }
  );

  // Infinite scroll for logs
  const { ref: loadMoreRef } = useIntersectionObserver({
    onIntersect: () => {
      if (hasMore && !isFetchingMore) {
        fetchMore();
      }
    },
    enabled: hasMore && !isFetchingMore && activeTab === 'logs',
  });

  // Extract data
  const apiKeys = useMemo(() => {
    const data = apiKeysData || {};
    return data.keys || data.apiKeys || data.data || data.items || [];
  }, [apiKeysData]);

  const webhooks = useMemo(() => {
    const data = webhooksData || {};
    return data.webhooks || data.data || data.items || [];
  }, [webhooksData]);

  const logs = useMemo(() => {
    return apiLogs || [];
  }, [apiLogs]);

  // Handlers
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  }, [showToast]);

  const handleCreateKey = useCallback(() => {
    if (!newKeyName.trim()) {
      showToast('Please enter a key name', 'error');
      return;
    }
    createKeyMutation.mutate(newKeyName.trim());
  }, [newKeyName, createKeyMutation, showToast]);

  const handleCreateWebhook = useCallback(() => {
    if (!newWebhook.url.trim()) {
      showToast('Please enter a webhook URL', 'error');
      return;
    }
    if (newWebhook.events.length === 0) {
      showToast('Please select at least one event', 'error');
      return;
    }
    createWebhookMutation.mutate(newWebhook);
  }, [newWebhook, createWebhookMutation, showToast]);

  const toggleWebhookEvent = useCallback((event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event],
    }));
  }, []);

  // Helpers
  function formatTimeAgo(timestamp) {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }

  function formatDate(timestamp) {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function getStatusClass(status) {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'info';
  }

  function getMethodClass(method) {
    const classes = {
      GET: 'get',
      POST: 'post',
      PUT: 'put',
      PATCH: 'patch',
      DELETE: 'delete',
    };
    return classes[method?.toUpperCase()] || 'get';
  }

  function maskKey(key) {
    if (!key) return '-';
    if (key.length <= 12) return key;
    return `${key.slice(0, 8)}...${key.slice(-4)}`;
  }

  return (
    <div className="developers-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Developer Tools</h1>
          <p className="page-subtitle">Manage API keys, webhooks, and integrations</p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="book-open"
            onClick={() => window.open('/docs', '_blank')}
          >
            View Docs
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'api-keys' ? 'active' : ''}`}
            onClick={() => setActiveTab('api-keys')}
          >
            <Icon name="key" size="sm" />
            API Keys
          </button>
          <button
            className={`tab ${activeTab === 'webhooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('webhooks')}
          >
            <Icon name="zap" size="sm" />
            Webhooks
          </button>
          <button
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            <Icon name="activity" size="sm" />
            API Logs
          </button>
        </div>
      </div>

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">API Keys</h3>
            <Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => setShowCreateKeyModal(true)}
            >
              Create API Key
            </Button>
          </div>

          <div className="api-keys-list">
            {loadingKeys && apiKeys.length === 0 ? (
              // Skeleton loading
              <div className="api-keys-loading">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="api-key-card skeleton">
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                    <div className="skeleton-key"></div>
                  </div>
                ))}
              </div>
            ) : apiKeys.length > 0 ? (
              apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="api-key-card">
                  <div className="api-key-info">
                    <div className="api-key-name">{apiKey.name}</div>
                    <div className="api-key-meta">
                      Created {formatDate(apiKey.createdAt || apiKey.created)} •
                      Last used {formatTimeAgo(apiKey.lastUsedAt || apiKey.lastUsed) || 'never'}
                    </div>
                  </div>
                  <div className="api-key-value">
                    <code>{maskKey(apiKey.key || apiKey.prefix)}</code>
                    <button
                      className="copy-btn"
                      onClick={() => copyToClipboard(apiKey.key || apiKey.prefix || '')}
                      aria-label="Copy API key"
                    >
                      <Icon name="clipboard" size="sm" />
                    </button>
                  </div>
                  <div className="api-key-actions">
                    <span className={`status-dot ${apiKey.status || 'active'}`}></span>
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="trash"
                      iconOnly
                      onClick={() => revokeKeyMutation.mutate(apiKey.id)}
                      disabled={revokeKeyMutation.isLoading}
                      aria-label="Revoke key"
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="api-keys-empty">
                <Icon name="key" size="xl" />
                <p>No API keys yet</p>
                <span>Create your first API key to get started</span>
                <Button
                  variant="primary"
                  size="sm"
                  icon="plus"
                  onClick={() => setShowCreateKeyModal(true)}
                >
                  Create API Key
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">Webhooks</h3>
            <Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => setShowCreateWebhookModal(true)}
            >
              Add Webhook
            </Button>
          </div>

          <div className="webhooks-list">
            {loadingWebhooks && webhooks.length === 0 ? (
              <div className="webhooks-loading">
                {[1, 2].map((i) => (
                  <div key={i} className="webhook-card skeleton">
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-badges"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : webhooks.length > 0 ? (
              webhooks.map((webhook) => (
                <div key={webhook.id} className="webhook-card">
                  <div className="webhook-info">
                    <div className="webhook-url">
                      <code>{webhook.url}</code>
                    </div>
                    <div className="webhook-events">
                      {(webhook.events || []).map((event, idx) => (
                        <span key={idx} className="event-badge">{event}</span>
                      ))}
                    </div>
                  </div>
                  <div className="webhook-actions">
                    <span className={`status-dot ${webhook.status || 'active'}`}></span>
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="play"
                      iconOnly
                      onClick={() => testWebhookMutation.mutate(webhook.id)}
                      disabled={testWebhookMutation.isLoading}
                      aria-label="Test webhook"
                    />
                    <Button
                      variant="ghost"
                      size="xs"
                      icon="trash"
                      iconOnly
                      onClick={() => deleteWebhookMutation.mutate(webhook.id)}
                      disabled={deleteWebhookMutation.isLoading}
                      aria-label="Delete webhook"
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="webhooks-empty">
                <Icon name="zap" size="xl" />
                <p>No webhooks configured</p>
                <span>Add a webhook to receive real-time events</span>
                <Button
                  variant="primary"
                  size="sm"
                  icon="plus"
                  onClick={() => setShowCreateWebhookModal(true)}
                >
                  Add Webhook
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* API Logs Tab */}
      {activeTab === 'logs' && (
        <section className="section">
          <div className="section-header">
            <h3 className="section-title">API Request Logs</h3>
            <Button
              variant="secondary"
              size="sm"
              icon="refresh"
              onClick={refetchLogs}
              disabled={loadingLogs}
            >
              {loadingLogs ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <div className="api-logs">
            {loadingLogs && logs.length === 0 ? (
              <div className="logs-loading">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="log-entry skeleton">
                    <div className="skeleton-method"></div>
                    <div className="skeleton-path"></div>
                    <div className="skeleton-status"></div>
                  </div>
                ))}
              </div>
            ) : logs.length > 0 ? (
              <>
                {logs.map((log, index) => (
                  <div
                    key={log.id || index}
                    className={`log-entry ${getStatusClass(log.status || log.statusCode || 200)}`}
                  >
                    <span className={`log-method ${getMethodClass(log.method)}`}>
                      {log.method || 'GET'}
                    </span>
                    <span className="log-path">{log.path || log.endpoint || '/api/v1/...'}</span>
                    <span className="log-status">{log.status || log.statusCode || 200}</span>
                    <span className="log-time">{log.duration || log.latency || 0}ms</span>
                    <span className="log-timestamp">
                      {formatTimeAgo(log.createdAt || log.timestamp)}
                    </span>
                  </div>
                ))}

                {/* Infinite scroll trigger */}
                {hasMore && (
                  <div ref={loadMoreRef} className="logs-load-more">
                    {isFetchingMore ? (
                      <div className="loading-spinner">Loading more...</div>
                    ) : (
                      <span>Scroll for more</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="logs-empty">
                <Icon name="activity" size="xl" />
                <p>No API logs yet</p>
                <span>API requests will appear here in real-time</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick Start Guide */}
      <section className="section">
        <div className="section-header">
          <h3 className="section-title">Quick Start</h3>
        </div>

        <div className="code-block">
          <div className="code-header">
            <span>Install SDK</span>
            <button className="copy-btn" onClick={() => copyToClipboard('npm install @auth1/sdk')}>
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <pre><code>npm install @auth1/sdk</code></pre>
        </div>

        <div className="code-block">
          <div className="code-header">
            <span>Initialize</span>
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(`import { Auth1 } from '@auth1/sdk';\n\nconst auth1 = new Auth1({\n  apiKey: 'YOUR_API_KEY',\n  tenantId: 'YOUR_TENANT_ID',\n});`)}
            >
              <Icon name="clipboard" size="sm" />
            </button>
          </div>
          <pre><code>{`import { Auth1 } from '@auth1/sdk';

const auth1 = new Auth1({
  apiKey: 'YOUR_API_KEY',
  tenantId: 'YOUR_TENANT_ID',
});`}</code></pre>
        </div>
      </section>

      {/* Create API Key Modal */}
      {showCreateKeyModal && (
        <Modal
          title={createdKey ? 'API Key Created' : 'Create API Key'}
          onClose={() => {
            setShowCreateKeyModal(false);
            setCreatedKey(null);
            setNewKeyName('');
          }}
        >
          {createdKey ? (
            <div className="created-key-display">
              <p className="created-key-warning">
                Make sure to copy your API key now. You won't be able to see it again!
              </p>
              <div className="created-key-value">
                <code>{createdKey}</code>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="clipboard"
                  onClick={() => copyToClipboard(createdKey)}
                >
                  Copy
                </Button>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCreateKeyModal(false);
                  setCreatedKey(null);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="create-key-form">
              <div className="form-group">
                <label htmlFor="keyName">Key Name</label>
                <input
                  id="keyName"
                  type="text"
                  placeholder="e.g., Production API Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreateKeyModal(false);
                    setNewKeyName('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateKey}
                  disabled={createKeyMutation.isLoading || !newKeyName.trim()}
                >
                  {createKeyMutation.isLoading ? 'Creating...' : 'Create Key'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Create Webhook Modal */}
      {showCreateWebhookModal && (
        <Modal
          title="Add Webhook"
          onClose={() => {
            setShowCreateWebhookModal(false);
            setNewWebhook({ url: '', events: [] });
          }}
        >
          <div className="create-webhook-form">
            <div className="form-group">
              <label htmlFor="webhookUrl">Webhook URL</label>
              <input
                id="webhookUrl"
                type="url"
                placeholder="https://your-server.com/webhook"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Events</label>
              <div className="events-grid">
                {WEBHOOK_EVENTS.map((event) => (
                  <label key={event.value} className="event-checkbox">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(event.value)}
                      onChange={() => toggleWebhookEvent(event.value)}
                    />
                    <span>{event.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateWebhookModal(false);
                  setNewWebhook({ url: '', events: [] });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateWebhook}
                disabled={createWebhookMutation.isLoading || !newWebhook.url.trim() || newWebhook.events.length === 0}
              >
                {createWebhookMutation.isLoading ? 'Creating...' : 'Create Webhook'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Developers;
